// ui/src/components/rep/MyCompensation.jsx
/**
 * @file MyCompensation.jsx
 * @module ui/src/components/rep/MyCompensation
 * @description Renders the rep's mobile-first primary compensation dashboard with attainment,
 * provisional earnings, next action, plan narrative, and credits.
 *
 * S804 fix: planId is now resolved from EP 48 dashboard response (data.planId) rather
 * than requiring the parent to pass it as a prop. The prop is still accepted for
 * backward compatibility but is no longer required — data.planId takes precedence.
 *
 * @session-6-migration-flags
 * - plan.effective_from / effective_to (m_tc_01): Indirect dependency through C-38 RepPlanSummary.
 * - credit.economic_event_date / economic_date_basis (m_tc_04b): Indirect dependency through C-39 RepCreditList.
 *
 * @locked-rules
 * - XC-6: Provisional earnings distinguish calculated zero, unavailable, and partial.
 * - XC-7: Earnings and freshness states are explicitly named.
 * - INV-3: planStatus is never used for display logic.
 * - DP-8: Never render modifier-level CA-2 approval language.
 * - C-37: Three primary cards remain visible in the loaded dashboard.
 *
 * @accessibility
 * - Root is role="main".
 * - Each primary summary card is a named region.
 * - Navigation buttons have explicit action labels.
 *
 * @dependencies
 * - C-38 RepPlanSummary.
 * - C-39 RepCreditList.
 * - C-44 ProvisionalBadge.
 * - C-46 DataFreshnessIndicator.
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import RepCreditList from './RepCreditList';
import ProvisionalBadge from '../shared/ProvisionalBadge';
import DataFreshnessIndicator from '../shared/DataFreshnessIndicator';

/**
 * @typedef {Object} MyCompensationProps
 * @property {string} positionId
 * @property {number} fiscalPeriodId
 * @property {number} [planId] — optional; resolved from EP 48 response when not provided
 * @property {function(): void} [onViewPayoutExplanation]
 * @property {function(): void} [onViewHistory]
 */

function formatCurrency(value, currencyCode) {
  if (value === null || value === undefined) return '—';

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode || 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';

  return `${new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

function MyCompensation({
  positionId,
  fiscalPeriodId,
  planId = undefined,
  onViewPayoutExplanation = undefined,
  onViewHistory = undefined,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    fetch(
      `/api/v1/rep/${encodeURIComponent(
        positionId
      )}/dashboard?period=${encodeURIComponent(fiscalPeriodId)}`,
      { signal: controller.signal }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Unable to load compensation dashboard (${response.status}).`
          );
        }
        return response.json();
      })
      .then((payload) => {
        setData(payload);
      })
      .catch((fetchError) => {
        if (fetchError.name !== 'AbortError') {
          setError(fetchError);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [positionId, fiscalPeriodId, retryKey]);

  if (loading) {
    return (
      <main
        className="my-compensation"
        role="main"
        aria-label="My compensation"
        aria-busy="true"
      >
        <div className="my-compensation__summary-grid">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="skeleton my-compensation__card-skeleton"
            />
          ))}
        </div>
        <style>{styles}</style>
      </main>
    );
  }

  if (error) {
    return (
      <main className="my-compensation" role="main">
        <section
          className="my-compensation__error"
          role="alert"
          data-state="error"
        >
          <h2>Unable to load compensation</h2>
          <p>{error.message}</p>
          <button
            type="button"
            className="secondary-button"
            onClick={() => setRetryKey((value) => value + 1)}
          >
            Retry
          </button>
        </section>
        <style>{styles}</style>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="my-compensation" role="main">
        <div className="my-compensation__empty">
          Compensation information is not available.
        </div>
        <style>{styles}</style>
      </main>
    );
  }

  const earnings = data.earnings ?? {};
  const payoutAvailable =
    earnings.provisionalPayout !== null &&
    earnings.provisionalPayout !== undefined;
  const partialPayout = earnings.provisionalState === 'partial';

  // OI-S1051-REP-LOCAL-CURRENCY: show local currency when rep's local currency
  // differs from the reporting (base) currency (e.g. AUD rep on USD plan).
  // EP 48 now returns totalPayoutLocal + localCurrencyCode from payout_summary.
  // Fall back to provisionalPayout / payoutCurrencyCode when local fields absent.
  const displayPayoutAmount =
    earnings.totalPayoutLocal !== null &&
    earnings.totalPayoutLocal !== undefined &&
    earnings.localCurrencyCode &&
    earnings.localCurrencyCode !== earnings.payoutCurrencyCode
      ? earnings.totalPayoutLocal
      : earnings.provisionalPayout;
  const displayCurrencyCode =
    earnings.totalPayoutLocal !== null &&
    earnings.totalPayoutLocal !== undefined &&
    earnings.localCurrencyCode &&
    earnings.localCurrencyCode !== earnings.payoutCurrencyCode
      ? earnings.localCurrencyCode
      : earnings.payoutCurrencyCode;

  // S804: resolve planId from EP 48 response; fall back to prop for backward compat.
  // Number.isFinite guards against null/undefined/string from either source.
  const resolvedPlanId = Number.isFinite(data.planId)
    ? data.planId
    : Number.isFinite(planId)
    ? planId
    : null;

  return (
    <main
      className="my-compensation"
      role="main"
      aria-labelledby="my-compensation-title"
    >
      <div className="my-compensation__summary-grid">
        <section
          className="my-compensation__card"
          role="region"
          aria-label="Quota Attainment"
        >
          <p className="my-compensation__card-label">Quota Attainment</p>
          <strong className="my-compensation__large-value">
            {formatPercent(data.attainment?.attainmentPct)}
          </strong>

          <dl className="my-compensation__compact-metrics">
            <div>
              <dt>Quota</dt>
              <dd>
                {formatCurrency(
                  data.attainment?.quotaReporting,
                  data.attainment?.currencyCode
                )}
              </dd>
            </div>
            <div>
              <dt>Credits</dt>
              <dd>
                {formatCurrency(
                  data.attainment?.creditTotalReporting,
                  data.attainment?.currencyCode
                )}
              </dd>
            </div>
          </dl>
        </section>

        <section
          className="my-compensation__card"
          role="region"
          aria-label="Provisional Earnings"
          data-state={
            !payoutAvailable
              ? 'unavailable'
              : partialPayout
                ? 'partial'
                : 'calculated'
          }
        >
          <div className="my-compensation__card-topline">
            <p className="my-compensation__card-label">
              Provisional Earnings
            </p>

            {earnings.freshnessAt ? (
              <ProvisionalBadge
                freshnessTimestamp={earnings.freshnessAt}
                label="Provisional"
                completenessState={
                  earnings.provisionalState === 'full' ? 'full' : 'partial'
                }
              />
            ) : null}
          </div>

          <strong className="my-compensation__large-value">
            {payoutAvailable
              ? formatCurrency(
                  displayPayoutAmount,
                  displayCurrencyCode
                )
              : 'Not available'}
          </strong>

          {partialPayout && payoutAvailable ? (
            <span className="my-compensation__partial">Partial</span>
          ) : null}

          {data.dataFreshness?.projection ? (
            <div className="my-compensation__projection-freshness">
              <DataFreshnessIndicator
                signal="projection"
                freshnessAt={data.dataFreshness.projection.freshnessAt}
                status={data.dataFreshness.projection.status}
              />
            </div>
          ) : null}
        </section>

        <section
          className="my-compensation__card"
          role="region"
          aria-label="Next Threshold"
        >
          <p className="my-compensation__card-label">Next Threshold</p>

          {earnings.nextThreshold ? (
            <>
              <strong className="my-compensation__threshold-value">
                {formatCurrency(
                  earnings.nextThreshold.amountNeeded,
                  earnings.nextThreshold.currencyCode
                )}
              </strong>
              <p className="my-compensation__threshold-copy">
                more to {earnings.nextThreshold.label}
              </p>
            </>
          ) : (
            <strong className="my-compensation__threshold-value">
              At maximum tier
            </strong>
          )}
        </section>
      </div>

      <section
        className="my-compensation__freshness"
        aria-labelledby="my-compensation-freshness-title"
      >
        <h2 id="my-compensation-freshness-title">Data freshness</h2>

        <div className="my-compensation__freshness-grid">
          {data.dataFreshness?.quota ? (
            <DataFreshnessIndicator
              signal="quota"
              freshnessAt={data.dataFreshness.quota.freshnessAt}
              status={data.dataFreshness.quota.status}
            />
          ) : null}

          {data.dataFreshness?.credits ? (
            <DataFreshnessIndicator
              signal="credits"
              freshnessAt={data.dataFreshness.credits.freshnessAt}
              status={data.dataFreshness.credits.status}
            />
          ) : null}
        </div>
      </section>

      {/* Actions + Plan strip — single row (S805: replaced RepPlanSummary dead section) */}
      <div className="my-compensation__actions-row">
        <div className="my-compensation__actions" aria-label="Compensation navigation">
          {onViewPayoutExplanation ? (
            <button
              type="button"
              className="secondary-button"
              onClick={onViewPayoutExplanation}
            >
              View full payout explanation
            </button>
          ) : null}

          {onViewHistory ? (
            <button
              type="button"
              className="secondary-button"
              onClick={onViewHistory}
            >
              View history
            </button>
          ) : null}
        </div>

        {/* PlanAcknowledgmentStrip — S805 Priority 3: inline one-liner replacing dead RepPlanSummary */}
        <PlanAcknowledgmentStrip
          planName={data.planSummaryPayload?.planName ?? data.planName ?? null}
          planVersionLabel={data.planSummaryPayload?.planVersionLabel ?? data.planVersionLabel ?? null}
          fiscalPeriodId={fiscalPeriodId}
        />
      </div>

      <section
        className="my-compensation__content-section"
        aria-labelledby="my-compensation-credits-title"
      >
        <h2
          id="my-compensation-credits-title"
          className="my-compensation__section-heading"
        >
          Your credits
        </h2>

        <RepCreditList
          positionId={positionId}
          fiscalPeriodId={fiscalPeriodId}
        />
      </section>

      <style>{styles}</style>
    </main>
  );
}

MyCompensation.propTypes = {
  positionId: PropTypes.string.isRequired,
  fiscalPeriodId: PropTypes.number.isRequired,
  planId: PropTypes.number,
  onViewPayoutExplanation: PropTypes.func,
  onViewHistory: PropTypes.func,
};

// MyCompensation.defaultProps removed S805 — defaults in function signature per React 18.3+

// ---------------------------------------------------------------------------
// PlanAcknowledgmentStrip — S805 Priority 3
// Inline one-liner: plan name · version · Acknowledged Yes/No · date
// Sits beside the action buttons, replacing the dead RepPlanSummary section.
// Fetches GET /api/v1/my/plan-acknowledgment?fiscal_period_id={id}
// Plan name/version sourced from EP 48 dashboard response passed as props.
// ---------------------------------------------------------------------------

function PlanAcknowledgmentStrip({ planName, planVersionLabel, fiscalPeriodId }) {
  const [ack, setAck] = useState(null);

  useEffect(() => {
    if (!fiscalPeriodId) return;
    fetch(`/api/v1/my/plan-acknowledgment?fiscal_period_id=${fiscalPeriodId}`, {
      credentials: 'include',
    })
      .then((r) => r.ok ? r.json() : null)
      .then(setAck)
      .catch(() => {});
  }, [fiscalPeriodId]);

  if (!planName && !planVersionLabel) return null;

  const acknowledged = ack?.acknowledged === true;
  const ackDate = ack?.acknowledged_at
    ? new Date(ack.acknowledged_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  return (
    <div className="my-compensation__plan-strip" aria-label="Current plan">
      <span className="my-compensation__plan-strip-name">
        {planName ?? '—'}
        {planVersionLabel ? (
          <span className="my-compensation__plan-strip-version">{planVersionLabel}</span>
        ) : null}
      </span>
      <span className="my-compensation__plan-strip-sep" aria-hidden="true">·</span>
      <span className={`my-compensation__plan-strip-ack${acknowledged ? ' my-compensation__plan-strip-ack--yes' : ''}`}>
        {acknowledged ? `Acknowledged${ackDate ? ` ${ackDate}` : ''}` : 'Not acknowledged'}
      </span>
    </div>
  );
}

const styles = `
  .my-compensation {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 16px;
    color: rgb(var(--color-text));
  }

  .my-compensation__page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .my-compensation__eyebrow {
    margin: 0 0 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .my-compensation__page-header h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
  }

  .my-compensation__period {
    padding: 2px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .my-compensation__summary-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .my-compensation__card {
    min-width: 0;
    padding: 16px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
  }

  .my-compensation__card-label {
    margin: 0;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .my-compensation__card-topline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .my-compensation__large-value {
    display: block;
    margin-top: 9px;
    font-size: 28px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .my-compensation__compact-metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin: 14px 0 0;
  }

  .my-compensation__compact-metrics dt {
    margin-bottom: 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    text-transform: uppercase;
  }

  .my-compensation__compact-metrics dd {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
  }

  .my-compensation__partial {
    display: inline-flex;
    margin-top: 8px;
    padding: 2px 10px;
    border: 1px solid rgb(var(--color-warning) / 0.3);
    border-radius: 999px;
    background: rgb(var(--color-warning) / 0.07);
    color: rgb(var(--color-warning));
    font-size: 11px;
    font-weight: 600;
  }

  .my-compensation__projection-freshness {
    margin-top: 12px;
  }

  .my-compensation__threshold-value {
    display: block;
    margin-top: 9px;
    font-size: 22px;
    font-weight: 600;
  }

  .my-compensation__threshold-copy {
    margin: 4px 0 0;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .my-compensation__freshness,
  .my-compensation__content-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .my-compensation__freshness h2,
  .my-compensation__content-section > h2 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
  }

  .my-compensation__freshness-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  /* Actions row — buttons + plan strip side by side (S805) */
  .my-compensation__actions-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    justify-content: space-between;
  }

  .my-compensation__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  /* PlanAcknowledgmentStrip (S805 Priority 3) */
  .my-compensation__plan-strip {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    background: rgb(var(--color-surface));
    font-size: 12px;
    color: rgb(var(--color-text-muted));
  }

  .my-compensation__plan-strip-name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: rgb(var(--color-text));
  }

  .my-compensation__plan-strip-version {
    padding: 1px 7px;
    border: 1px solid rgb(var(--color-primary) / 0.25);
    border-radius: 999px;
    background: rgb(var(--color-primary) / 0.06);
    color: rgb(var(--color-primary));
    font-size: 10px;
    font-weight: 600;
  }

  .my-compensation__plan-strip-sep {
    color: rgb(var(--color-border));
  }

  .my-compensation__plan-strip-ack { color: rgb(var(--color-text-muted)); }
  .my-compensation__plan-strip-ack--yes { color: rgb(var(--color-success)); }

  .my-compensation button:focus-visible {
    outline: 2px solid rgb(var(--color-focus-ring));
    outline-offset: 2px;
  }

  .my-compensation__empty {
    padding: 24px 16px;
    border: 1px dashed rgb(var(--color-border));
    border-radius: 8px;
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .my-compensation__error {
    padding: 16px 12px;
    border: 1px solid rgb(var(--color-danger) / 0.2);
    border-radius: 8px;
    background: rgb(var(--color-danger) / 0.05);
  }

  .my-compensation__error h2 {
    margin: 0 0 5px;
    font-size: 13px;
  }

  .my-compensation__error p {
    margin: 0 0 12px;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .my-compensation__card-skeleton {
    height: 170px;
    border-radius: 12px;
  }

  @media (min-width: 820px) {
    .my-compensation__summary-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
`;

export default MyCompensation;
